import { Box, Button, Flex, Icon, Spinner, Text, Tooltip } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { AiOutlineInstagram } from 'react-icons/ai';
import { IoLogoFacebook } from 'react-icons/io';
import { IoPhonePortrait, IoSquareOutline } from "react-icons/io5";
import { MdFacebook, MdOutlinePhonelink } from 'react-icons/md';
import { RiMessengerLine } from "react-icons/ri";
import { TbRectangle, TbRectangleVertical } from "react-icons/tb";
import { useGetAdPreviewsQuery } from 'store/campaigns/adsApi';
import { removeEmptyValues } from 'utils/functions';
import imagePlacementConfig from 'utils/imagePlacementConfig.json';
import videoPlacementConfig from 'utils/videoPlacementConfig.json';
import adFormatMapping from 'utils/adFormatMapping.json';
import placementMapping from 'utils/placementMapping.json';

const iconMapping = {
    "AiOutlineInstagram": <AiOutlineInstagram />,
    "MdFacebook": <MdFacebook />,
    "RiMessengerLine": <RiMessengerLine />,
    "IoLogoFacebook": <IoLogoFacebook />,
    "IoPhonePortrait": <IoPhonePortrait />
};

const AdFormatDisplay = ({ ad }) => {
    const adFormat = adFormatMapping[ad.format] || { name: 'No Format', icon: null };
    
    return (
      <Flex alignItems="center" gap={2}>
        {/* Display the icon */}
        <Box>
          {iconMapping[adFormat.icon] || null}
        </Box>
  
        {/* Display the ad format name */}
        <Text py={4} fontSize={'12px'}>
          {adFormat.name || 'No Format'}
        </Text>
      </Flex>
    );
  };


// Mapping function to get the selected placements based on platform type
const getSelectedPlacements = (platformType) => {
    switch (platformType) {
        case 'all':
            return [
                "INSTAGRAM_REELS",
                "INSTAGRAM_STORY",
                "INSTAGRAM_EXPLORE_CONTEXTUAL",
                "INSTAGRAM_PROFILE_REELS",
                "INSTAGRAM_PROFILE_FEED",
                "MOBILE_FEED_BASIC",
                "FACEBOOK_REELS_MOBILE",
                "INSTAGRAM_REELS_OVERLAY",
                "FACEBOOK_PROFILE_FEED_MOBILE",
                "SUGGESTED_VIDEO_MOBILE",
                "INSTREAM_VIDEO_MOBILE",
                "MARKETPLACE_MOBILE",
                "FACEBOOK_STORY_MOBILE",
                "MESSENGER_MOBILE_INBOX_MEDIA",
                "MESSENGER_MOBILE_STORY_MEDIA"
            ];
        case 'tab':
            return [
                "INSTAGRAM_SEARCH_GRID",
                "INSTAGRAM_EXPLORE_CONTEXTUAL",
                "INSTAGRAM_PROFILE_REELS",
                "INSTAGRAM_EXPLORE_GRID_HOME",
                "INSTAGRAM_PROFILE_FEED",
                "MOBILE_FEED_BASIC",
                "SUGGESTED_VIDEO_MOBILE",
                "MARKETPLACE_MOBILE",
                "MESSENGER_MOBILE_INBOX_MEDIA"
            ];
        case 'mobile':
            return [
                "INSTAGRAM_REELS",
                "INSTAGRAM_STORY",
                "FACEBOOK_REELS_MOBILE",
                "FACEBOOK_PROFILE_FEED_MOBILE",
                "INSTREAM_VIDEO_MOBILE",
                "FACEBOOK_STORY_MOBILE",
                "MOBILE_INTERSTITIAL",
                "MESSENGER_MOBILE_STORY_MEDIA"
            ];
        case 'desktop':
            return [
                "RIGHT_COLUMN_STANDARD",
                "MARKETPLACE_SEARCH_ADS_MOBILE"
            ];
        default:
            return [];
    }
};

const formatPhoneNumber = (phoneNumber) => {

    if (!phoneNumber.startsWith('tel:+')) {
        return `tel:+${phoneNumber}`;
    }
    return phoneNumber;
};

function CreateSizeAdPreviews({adFormik, placements, file, adsAccountData }) {

    const [selectedPlacements, setSelectedPlacements] = useState([]);
    const [creative, setCreative] = useState(null);
    const [selectedPlatform, setSelectedPlatform] = useState('all');    

    const filteredCreative = (() => {
        if (!creative) return null;
    
        const clone = JSON.parse(JSON.stringify(creative)); // deep clone
    
        // Remove thumbnail_url from images
        if (clone?.asset_feed_spec?.images) {
            clone.asset_feed_spec.images = clone.asset_feed_spec.images.map(({ thumbnail_url, creative_hash, ...rest }) => rest);
        }
    
        // Remove thumbnail_url from videos
        if (clone?.asset_feed_spec?.videos) {
            clone.asset_feed_spec.videos = clone.asset_feed_spec.videos.map(({ thumbnail_url, hash, ...rest }) => rest);
        }
    
        // Add age_min and age_max to each customization_spec
        if (Array.isArray(clone?.asset_feed_spec?.asset_customization_rules)) {
            clone.asset_feed_spec.asset_customization_rules = clone.asset_feed_spec.asset_customization_rules.map(rule => ({
                ...rule,
                customization_spec: {
                    ...rule.customization_spec,
                    age_min: 13,
                    age_max: 65
                }
            }));
        }
    
         // Remove all empty values
        const cleaned = removeEmptyValues(clone);

        return cleaned;
    })();

    const updatedCreative = encodeURIComponent(JSON.stringify(filteredCreative));

    const shouldFetchAdPreview = updatedCreative &&
        adsAccountData && selectedPlacements.length > 0;
        
    const { data: AdPreview, isLoading, error } = useGetAdPreviewsQuery(
        {
            adsAccountId: adsAccountData?.account_id,
            creative: updatedCreative,
            ad_format: selectedPlacements, 
        },
        { skip: !shouldFetchAdPreview}
    );

    // Constructing the creative object from the file data
    useEffect(() => {
        if (file) {
            let creative = {};

            const storySpec = {
                page_id: file.creative.object_story_spec.page_id,
                instagram_user_id: file.creative.object_story_spec.instagram_user_id
            };
    
            const contextual_multi_ads = {
                enroll_status: file?.creative.enroll_status
            }

            const titles = file.creative.object_story_spec.link_data.name
                ? [{ text: file.creative.object_story_spec.link_data.name }]
                : undefined;
        
            const bodies = file.creative.object_story_spec.link_data.message
                ? [{ text: file.creative.object_story_spec.link_data.message }]
                : undefined;

            const descriptions = file.creative.object_story_spec.link_data.description
                ? [{ text: file.creative.object_story_spec.link_data.description }]
                : undefined;

            const call_to_action_types = [file.creative.object_story_spec.link_data.call_to_action.type];

            const call_to_actions = [
                ...(file.creative.object_story_spec.link_data.call_to_action.type === 'CALL_NOW' ? [{
                  type: file.creative.object_story_spec.link_data.call_to_action.type,
                  value: {
                    link: `tel:+${file.creative.object_story_spec.link_data.call_to_action?.value?.link?.replace(/^(\+)?/, '')}`
                  }
                }] : []),
              
                ...(file.creative.object_story_spec.link_data.destination_type === 'ON_AD' ? [{
                  type: file.creative.object_story_spec.link_data.call_to_action.type,
                  value: {
                    lead_gen_form_id: file.creative.object_story_spec.link_data.call_to_action?.value?.link
                  }
                }] : [])
            ];

            const websiteUrl = file.creative.object_story_spec.link_data.destination_type === 'PHONE_CALL'
                                ? `https://facebook.com/${file.creative.object_story_spec.page_id || ''}/`
                                : file.creative.object_story_spec.link_data.destination_type === 'ON_AD'
                                ? 'http://fb.me/'
                                : file.creative.object_story_spec.link_data.destination_type === 'APP'
                                    ? file.creative.website_url
                                    : file.creative.object_story_spec.link_data.link || '';

            const link_urls = [
                            {
                                website_url: websiteUrl, 
                                ...(file.creative.object_story_spec.link_data.destination_type === 'APP' && { deeplink_url: file.creative.object_story_spec.link_data.link }),
                                ...(!['CALL_NOW','LIKE_PAGE'].includes(file.creative.object_story_spec.link_data.call_to_action.type) && {display_url: file.creative.object_story_spec.link_data.caption})
                            }
                        ];

            
            const app_product_page_id = file.creative.app_product_page_id;
            // const degrees_of_freedom_spec = {creative_features_spec: {
            //     standard_enhancements: {
            //         enroll_status: "OPT_OUT",
            //     }
            // }};

              // Step 1: Create a hash -> { mediaType, placement } map
              const hashMap = {};
              file?.mediaUrls?.forEach(item => {
                  hashMap[item.hash] = {
                      mediaType: item.mediaType,
                      placement: item.placement
                  };
              });
      
              // Step 2: Deduplicate mediaData based on hash
              const seenHashes = new Set();
              const hashDuplicates = new Map();
              
              // First pass: gather duplicates by hash
              file?.mediaUrls?.forEach(item => {
                  if (seenHashes.has(item.hash)) {
                      if (!hashDuplicates.has(item.hash)) {
                          hashDuplicates.set(item.hash, []);
                      }
                      hashDuplicates.get(item.hash).push(item.placement);
                  } else {
                      seenHashes.add(item.hash);
                  }
              });
              
              // Reset seenHashes to reuse it
              seenHashes.clear();
              
              // Second pass: build mediaData with tags
              const mediaData = file?.mediaUrls?.filter(item => {
                      if (seenHashes.has(item.hash)) return false;
                      seenHashes.add(item.hash);
                      return true;
                  })
                  .map(item => {
                      const duplicatePlacements = hashDuplicates.get(item.hash);
                      const tag = duplicatePlacements?.length
                          ? { duplicatePlacements }
                          : undefined;
              
                      return {
                            [item.mediaType === 'image' ? 'hash' : 'video_id']: item.hash,
                            thumbnail_url: item.url,
                            [item.mediaType === 'image' ? 'creative_hash' : 'hash'] : item.mediaHash,
                            mediaType: item.mediaType,
                            adlabels: [{ name: item.placement }],
                            ...(tag && { tag })
                      };
              });        
      
              // Remove 'tag' before assigning
              const cleanedMediaData = mediaData?.map(({ tag, mediaType, creative_hash, thumbnail_url,  ...rest }) => rest);
      
              // Step 3: Build asset_customization_rules using hashMap
              const asset_customization_rules = [];
      
              mediaData?.forEach(media => {
                  const meta = hashMap[media.mediaType === 'image' ? media.hash : media.video_id];
                  if (!meta) return; // safety check
              
                  const { mediaType } = meta;
                  const isDuplicate = media.tag?.duplicatePlacements?.length > 0;
              
                  (media?.adlabels || []).forEach(label => {
                      const placement = label.name;
                      if (!placement) return;
              
                      asset_customization_rules?.push({
                          [mediaType === 'image' ? 'image_label' : 'video_label']: { name: placement },
                          customization_spec: isDuplicate
                              ? {}
                              : (mediaType === 'image' ? imagePlacementConfig : videoPlacementConfig)[placement] || {}
                      });
                  });
              });            

            if (file?.adType === 'image') {
                creative = {
                    name: file?.name,
                    thumbnail_url: file?.image_url,
                    object_story_spec: storySpec,
                    contextual_multi_ads,
                    asset_feed_spec: {
                        ad_formats: [
                            "SINGLE_IMAGE"
                        ],
                        images: cleanedMediaData,
                        asset_customization_rules : asset_customization_rules,
                        call_to_action_types,
                        ...(Array.isArray(call_to_actions) && call_to_actions.length > 0 && {
                            call_to_actions,
                        }),
                        link_urls,
                        app_product_page_id,
                        optimization_type : "PLACEMENT",
                        additional_data: {
                            "multi_share_end_card": false,
                            "is_click_to_message": false
                        },
                        reasons_to_shop: false,
                        shops_bundle : false,
                        ...(titles && { titles }),
                        ...(bodies && { bodies }),
                        ...(descriptions && { descriptions })
                    },
                    // degrees_of_freedom_spec,
                };
            } else {
                creative = {
                    name: file?.name,
                    thumbnail_url: file?.image_url,
                    object_story_spec: storySpec,
                    contextual_multi_ads,
                    asset_feed_spec: {
                        ad_formats: [
                            "SINGLE_VIDEO"
                        ],
                        videos: cleanedMediaData,
                        asset_customization_rules : asset_customization_rules,
                        call_to_action_types,
                        ...(Array.isArray(call_to_actions) && call_to_actions.length > 0 && {
                            call_to_actions,
                        }),
                        link_urls,
                        app_product_page_id,
                        optimization_type : "PLACEMENT",
                        additional_data: {
                            "multi_share_end_card": false,
                            "is_click_to_message": false
                        },
                        reasons_to_shop: false,
                        shops_bundle : false,
                        ...(titles && { titles }),
                        ...(bodies && { bodies }),
                    }
                };
            }
            setCreative(creative);
        }
    }, [file]);

    // Helper function to unescape the string
    const unescapeHtml = (str) => {
        const string = str.replace(/\\"/g, '"').replace(/\\'/g, "'").replace(/\\\\/g, '\\');
        return string;
    };

    // Helper function to map raw positions to placement names
    const mapPositionsToPlacements = (platform, positions) => {
        const platformMapping = placementMapping[platform];
        return positions.map(position => platformMapping[position]);
    };

    // Handle icon button clicks to set the selected placements
    const handleIconClick = (platformType) => {
        setSelectedPlatform(platformType);

        // Get the raw positions from the parent
        const rawPositions = {
            facebook: placements?.targeting?.facebook_positions || [],
            instagram: placements?.targeting?.instagram_positions || [],
            audienceNetwork: placements?.targeting?.audience_network_positions || [],
            messenger: placements?.targeting?.messenger_positions || [],
        };

        // If rawPositions is empty (i.e., no positions are available), fallback to all placements
        if (!rawPositions.facebook.length && !rawPositions.instagram.length && !rawPositions.audienceNetwork.length && !rawPositions.messenger.length) {
            // Fallback to default "all" placements
            rawPositions.facebook = Object.keys(placementMapping.facebook);
            rawPositions.instagram = Object.keys(placementMapping.instagram);
            rawPositions.audienceNetwork = Object.keys(placementMapping.audienceNetwork);
            rawPositions.messenger = Object.keys(placementMapping.messenger);
        }

        // Map the raw positions to their mapped values
        const mappedPlacements = [
            ...mapPositionsToPlacements('instagram', rawPositions.instagram),
            ...mapPositionsToPlacements('facebook', rawPositions.facebook),
            ...mapPositionsToPlacements('audienceNetwork', rawPositions.audienceNetwork),
            ...mapPositionsToPlacements('messenger', rawPositions.messenger),
        ];

        // Get the selected placements based on platformType
        const newPlacements = getSelectedPlacements(platformType);

       // Only filter out the specific placements if video data exists
       let filteredPlacements = mappedPlacements.filter(placement => newPlacements.includes(placement));

       if (file?.adType === 'video') {
           // Filter out the unwanted placements when video_data is present
           filteredPlacements = filteredPlacements.filter(
               placement => placement !== 'INSTAGRAM_REELS_OVERLAY' && placement !== 'INSTAGRAM_SEARCH_GRID'
           );
       }

        setSelectedPlacements(filteredPlacements); // Update the selected placements based on intersection
    };

    useEffect(() => {
        // Set initial placements based on the 'all' selection
        if (selectedPlatform === 'all') {
            handleIconClick('all');
        }
    }, [selectedPlatform]);

    return (
        <Box>
            {/* Preview icons (All, Tablet, Mobile, Desktop) */}
            <Flex justifyContent="center" gap={6} my={4} wrap="wrap">
                <Tooltip label="All" fontSize="sm" placement="top">
                    <Button
                        onClick={() => handleIconClick('all')}
                        aria-label="All"
                        variant="solid"
                        boxSize={6}
                        colorScheme='brand'
                        bg={selectedPlatform === 'all' ? 'brand.800' : 'gray.400'}
                    >
                        <Icon as={MdOutlinePhonelink} boxSize={4} />
                    </Button>
                </Tooltip>
                <Tooltip label="Feeds, In-stream ads for videos and reels, Search results" fontSize="sm" placement="top">
                    <Button
                        onClick={() => handleIconClick('tab')}
                        aria-label="Tablet"
                        variant="solid"
                        boxSize={6}
                        colorScheme='brand'
                        bg={selectedPlatform === 'tab' ? 'brand.800' : 'gray.400'}
                    >
                        <Icon as={IoSquareOutline} boxSize={4} />
                    </Button>

                </Tooltip>
                <Tooltip label="Stories and Reels, Apps and sites" fontSize="sm" placement="top">
                    <Button
                        onClick={() => handleIconClick('mobile')}
                        aria-label="Mobile"
                        variant="solid"
                        boxSize={6}
                        colorScheme='brand'
                        bg={selectedPlatform === 'mobile' ? 'brand.800' : 'gray.400'}
                    >
                        <Icon as={TbRectangleVertical} boxSize={4} />
                    </Button>

                </Tooltip>
                <Tooltip label="A Right column, Search results" fontSize="sm" placement="top">
                    <Button
                        onClick={() => handleIconClick('desktop')}
                        aria-label="Desktop"
                        variant="solid"
                        boxSize={6}
                        colorScheme="brand"
                        bg={selectedPlatform === 'desktop' ? 'brand.800' : 'gray.400'}
                    >
                        <Icon as={TbRectangle} boxSize={4} />
                    </Button>
                </Tooltip>
            </Flex>

            {/* Ad Preview */}
            {isLoading ? (
                <Flex justifyContent={'center'} alignItems={'center'} height={'60vh'}>
                    <Spinner />
                </Flex>
            ) : error ? (
                <Box>Error loading ad preview!</Box>
            ) : AdPreview && AdPreview.length > 0 ? (
                <Box display="flex" flexWrap="wrap" overflowY={'auto'}>
                    {AdPreview.map((ad, index) => (
                        <Box key={index} width="50%">
                            <Box>
                                 <Flex alignItems="center" gap={2}>
                                    <AdFormatDisplay ad={ad}/>
                                </Flex>
                                {ad.body ? (
                                    <Box
                                        dangerouslySetInnerHTML={{ __html: unescapeHtml(ad.body) }}
                                        width="300px"
                                        height="560px"
                                        bg="#EFEFEF"
                                        boxShadow="0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)"
                                        display="flex"
                                        flexDirection="column"
                                        justifyContent="center"
                                        alignItems="center"
                                        borderRadius="8px"
                                        className='ad-preview-wrapper'
                                    />
                                ) : (
                                    <Box>No preview available for this format.</Box>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>
            ) : (
                <Box h={'500px'} display="flex" alignItems={'center'} justifyContent={'center'}>
                    Add media to see ad examples.
                </Box>
            )}
        </Box>
    );
}

export default CreateSizeAdPreviews;
